/**
 * ConfigPreview Component
 * Shows a live preview of how content will be generated with current settings
 */

import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Eye, RefreshCw, Sparkles } from 'lucide-react';
import type { ContentType, ResolvedConfig } from '@/hooks/useContentConfig';

interface StyleExample {
  aspect: string;
  setting: string;
  example: string;
}

interface SampleData {
  type: string;
  title: string;
  description?: string;
  price?: string;
  category?: string;
  brand?: string;
}

interface PreviewResponse {
  success: boolean;
  data: {
    resolved: ResolvedConfig;
    sources: Record<string, string>;
    promptInstructions: string;
    styleExamples: StyleExample[];
    compactSummary: string;
    sampleData?: SampleData;
    generatedContent?: string;
  };
}

interface ConfigPreviewProps {
  resolvedConfig: ResolvedConfig | null;
  onGeneratePreview: (contentType: ContentType) => Promise<PreviewResponse | null>;
  loading?: boolean;
}

const CONTENT_TYPE_OPTIONS: { value: ContentType; label: string }[] = [
  { value: 'category_aeo', label: 'Category AEO' },
  { value: 'product_aeo', label: 'Product AEO' },
  { value: 'product_description', label: 'Product Description' },
  { value: 'faq', label: 'FAQ Answer' },
  { value: 'ai_article', label: 'AI Article' },
];

export function ConfigPreview({ resolvedConfig, onGeneratePreview, loading }: ConfigPreviewProps) {
  const [selectedContentType, setSelectedContentType] = useState<ContentType>('product_description');
  const [generatedContent, setGeneratedContent] = useState<string | null>(null);
  const [sampleData, setSampleData] = useState<SampleData | null>(null);
  const [promptInstructions, setPromptInstructions] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const result = await onGeneratePreview(selectedContentType);
      if (result?.success && result?.data) {
        setGeneratedContent(result.data.generatedContent || null);
        setSampleData(result.data.sampleData || null);
        setPromptInstructions(result.data.promptInstructions || null);
      }
    } catch (err) {
      console.error('[ConfigPreview] Error generating preview:', err);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Eye className="h-5 w-5 text-indigo-500" />
            <CardTitle className="text-lg">Preview</CardTitle>
          </div>
          {resolvedConfig?.sources && (
            <div className="flex gap-1">
              {Object.entries(resolvedConfig.sources).map(([key, source]) => (
                <Badge key={key} variant="outline" className="text-xs">
                  {key}: {source}
                </Badge>
              ))}
            </div>
          )}
        </div>
        <CardDescription>
          See how content will be generated with your current configuration
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Config Summary */}
        {resolvedConfig?.tone && resolvedConfig?.pricing && resolvedConfig?.brand && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div className="p-2 rounded-md bg-muted/50">
              <span className="text-muted-foreground">Tone:</span>
              <span className="ml-1 font-medium capitalize">
                {resolvedConfig.tone.preset || 'Custom'}
              </span>
            </div>
            <div className="p-2 rounded-md bg-muted/50">
              <span className="text-muted-foreground">Perspective:</span>
              <span className="ml-1 font-medium">
                {resolvedConfig.tone.perspective === 'first-person-plural' ? 'We/Our' :
                 resolvedConfig.tone.perspective === 'second-person' ? 'You/Your' : 'They'}
              </span>
            </div>
            <div className="p-2 rounded-md bg-muted/50">
              <span className="text-muted-foreground">Prices:</span>
              <span className="ml-1 font-medium capitalize">
                {resolvedConfig.pricing.includePrices?.replace('_', ' ') || 'always'}
              </span>
            </div>
            <div className="p-2 rounded-md bg-muted/50">
              <span className="text-muted-foreground">CTA:</span>
              <span className="ml-1 font-medium capitalize">{resolvedConfig.brand.ctaStyle || 'soft-sell'}</span>
            </div>
          </div>
        )}

        {/* Content Type Selection */}
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <Label htmlFor="contentType" className="text-sm font-medium mb-2 block">
              Content Type
            </Label>
            <Select
              value={selectedContentType}
              onValueChange={(v) => setSelectedContentType(v as ContentType)}
            >
              <SelectTrigger id="contentType">
                <SelectValue placeholder="Select content type" />
              </SelectTrigger>
              <SelectContent>
                {CONTENT_TYPE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="pt-6">
            <Button
              onClick={handleGenerate}
              disabled={generating || loading || !resolvedConfig}
            >
              {generating ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4 mr-2" />
                  Generate Preview
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Preview Output */}
        <div className="min-h-[200px] rounded-md border bg-muted/30 p-4 overflow-auto max-h-[500px]">
          {generating ? (
            <div className="space-y-3">
              <div className="text-sm text-muted-foreground">Generating sample content...</div>
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-5/6" />
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="h-4 w-4/5" />
            </div>
          ) : generatedContent ? (
            <div className="space-y-4">
              {/* Sample product info */}
              {sampleData && (
                <div className="text-xs text-muted-foreground border-b pb-2 mb-3">
                  <span className="font-medium">Sample from your catalog:</span>{' '}
                  {sampleData.title}
                  {sampleData.category && <span className="ml-2">({sampleData.category})</span>}
                </div>
              )}

              {/* Generated content - main focus */}
              <div>
                <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-amber-500" />
                  Generated Content Preview
                </h4>
                <div className="prose prose-sm max-w-none bg-background p-4 rounded border">
                  <div className="text-sm leading-relaxed whitespace-pre-wrap">
                    {generatedContent}
                  </div>
                </div>
              </div>

              {/* Collapsible prompt instructions */}
              {promptInstructions && (
                <div>
                  <button
                    onClick={() => setShowInstructions(!showInstructions)}
                    className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
                  >
                    {showInstructions ? '▼' : '▶'} Show AI prompt instructions
                  </button>
                  {showInstructions && (
                    <pre className="mt-2 text-xs bg-muted/50 p-3 rounded border whitespace-pre-wrap font-mono text-muted-foreground">
                      {promptInstructions}
                    </pre>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-muted-foreground text-sm gap-2">
              <Sparkles className="h-8 w-8 opacity-30" />
              <p>Click "Generate Preview" to see AI-generated content</p>
              <p className="text-xs">Uses a random product from your catalog</p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
